import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { VisualSeed, VisualNode } from "@/hooks/useMapVisualData";
import { ROLE_COLORS, STAGE_LABELS } from "./constants";

interface PipelineOverviewProps {
  seeds: VisualSeed[];
  nodes: VisualNode[];
}

const STAGE_COLORS: Record<string, string> = {
  pending: "#374151",
  serp_research: "#6366f1",
  kg_assembly: "#8b5cf6",
  fan_out: "#a78bfa",
  outline_ready: "#eab308",
  draft: "#f97316",
  optimization: "#06b6d4",
  humanization: "#ec4899",
  ready: "#22c55e",
  published: "#10b981",
  failed: "#ef4444",
};

export function PipelineOverview({ seeds, nodes }: PipelineOverviewProps) {
  const [expandedSeed, setExpandedSeed] = useState<string | null>(null);

  return (
    <div className="space-y-3">
      {seeds.map(seed => {
        const seedNodes = nodes.filter(n => n.seed_id === seed.id);
        const total = seedNodes.length;
        if (total === 0) return null;

        const ready = seedNodes.filter(n => n.engine_stage === "ready" || n.engine_stage === "published").length;
        const failed = seedNodes.filter(n => n.engine_stage === "failed").length;
        const outlineReady = seedNodes.filter(n => n.engine_stage === "outline_ready").length;
        const pending = seedNodes.filter(n => !n.engine_stage || n.engine_stage === "pending").length;
        const inProgress = total - ready - failed - pending - outlineReady;
        const pct = Math.round((ready / total) * 100);

        const isExpanded = expandedSeed === seed.id;

        // Build segments for stacked bar
        const segments = [
          { stage: "ready", count: ready, color: "#22c55e" },
          { stage: "outline_ready", count: outlineReady, color: "#eab308" },
          { stage: "in_progress", count: inProgress, color: "#8b5cf6" },
          { stage: "failed", count: failed, color: "#ef4444" },
          { stage: "pending", count: pending, color: "#374151" },
        ].filter(s => s.count > 0);

        return (
          <div key={seed.id} className="rounded-lg border bg-card">
            <div
              className="p-3 cursor-pointer hover:bg-muted/30 transition-colors"
              onClick={() => setExpandedSeed(isExpanded ? null : seed.id)}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  <h3 className="font-medium text-sm">{seed.name}</h3>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">{ready}/{total}</span>
                  <span className={pct >= 80 ? "text-green-400 font-medium" : pct >= 30 ? "text-amber-400" : "text-muted-foreground"}>
                    {pct}%
                  </span>
                </div>
              </div>

              {/* Stacked progress bar */}
              <div className="h-3 rounded-full overflow-hidden flex bg-muted/50">
                {segments.map((seg, i) => (
                  <div
                    key={i}
                    className="h-full transition-all duration-500"
                    style={{
                      width: `${(seg.count / total) * 100}%`,
                      backgroundColor: seg.color,
                    }}
                    title={`${seg.stage}: ${seg.count}`}
                  />
                ))}
              </div>

              {/* Mini legend */}
              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                {ready > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> {ready} gotowy</span>}
                {outlineReady > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" /> {outlineReady} outline</span>}
                {inProgress > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-500" /> {inProgress} w toku</span>}
                {failed > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> {failed} blad</span>}
                {pending > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-600" /> {pending} oczekuje</span>}
              </div>
            </div>

            {/* Expanded node list */}
            {isExpanded && (
              <div className="border-t px-3 pb-3 pt-2">
                <div className="space-y-1">
                  {seedNodes.map(node => (
                    <div key={node.id} className="flex items-center gap-2 text-xs py-0.5">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: STAGE_COLORS[node.engine_stage || "pending"] || "#374151" }} />
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ROLE_COLORS[node.document_role] || "#666" }} />
                      <span className="truncate flex-1">{node.title}</span>
                      <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0">
                        {STAGE_LABELS[node.engine_stage || "pending"]}
                      </Badge>
                      {node.quality_score != null && (
                        <span className={`text-[9px] shrink-0 ${node.quality_score >= 70 ? "text-green-400" : "text-amber-400"}`}>
                          {node.quality_score}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
